import React, { ReactElement, RefObject, useCallback } from 'react';

import Text from '../Text';
import Tooltip from '../Tooltip';
import { DownArrow, StyledButton, StyledCheckbox, StyledFieldset, StyledText } from './styles';
import { FormMultiSelectProps } from './types';

const FormMultiSelectButton = React.forwardRef(
	(
		{
			placeholder,
			borderColor,
			contentAlignment,
			isDisabled,
			maxWidth,
			fontWeight,
			size,
			backgroundColor = 'multiSelectButtonBg',
			textColor = 'multiSelectButtonText',
			downArrowColor,
		}: Pick<
			FormMultiSelectProps,
			| 'backgroundColor'
			| 'borderColor'
			| 'contentAlignment'
			| 'isDisabled'
			| 'maxWidth'
			| 'textColor'
			| 'fontWeight'
			| 'size'
			| 'placeholder'
			| 'downArrowColor'
		>,
		ref,
	): ReactElement => {
		return (
			<StyledButton
				backgroundColor={backgroundColor}
				borderColor={borderColor}
				contentAlignment={contentAlignment}
				isDisabled={isDisabled}
				maxWidth={maxWidth}
				textColor={textColor}
				fontWeight={fontWeight}
				size={size}
				buttonRef={ref as RefObject<HTMLButtonElement | HTMLAnchorElement>}
			>
				<DownArrow $downArrowColor={downArrowColor} />
				<StyledText>{placeholder}</StyledText>
			</StyledButton>
		);
	},
);

const FormMultiSelectContent = ({
	'aria-label': ariaLabel,
	label,
	tooltipTextColor,
	options,
	name,
	state,
	onChange,
	tooltipTextSize,
}: Pick<
	FormMultiSelectProps,
	'aria-label' | 'label' | 'tooltipTextColor' | 'options' | 'name' | 'state' | 'onChange' | 'tooltipTextSize'
>): ReactElement => {
	const handleOnChange = useCallback(
		(value: string) => {
			return () => {
				const newState = state.includes(value) ? state.filter(option => option !== value) : [...state, value];
				onChange(newState, value);
			};
		},
		[state, onChange],
	);

	return (
		<StyledFieldset aria-label={ariaLabel}>
			{label ? (
				<Text el="legend" color={tooltipTextColor}>
					{label}
				</Text>
			) : null}
			{options.map(option => {
				const optionLabel = typeof option === 'string' ? option : option.label;
				const optionValue = typeof option === 'string' ? option : option.value;
				return (
					<StyledCheckbox
						key={optionValue}
						name={name}
						value={optionLabel}
						onChange={handleOnChange(optionLabel)}
						checked={state.includes(optionLabel)}
						textSize={tooltipTextSize}
					>
						{optionValue}
					</StyledCheckbox>
				);
			})}
		</StyledFieldset>
	);
};

const FormMultiSelect = ({
	tooltipBackgroundColor = 'multiSelectTooltipBg',
	tooltipTextColor = 'multiSelectTooltipText',
	...props
}: FormMultiSelectProps): ReactElement => {
	return (
		<div>
			<Tooltip
				placement="bottom-start"
				hideOnClick={true}
				trigger="focus click"
				hasInteractiveContent={true}
				backgroundColor={tooltipBackgroundColor}
				textColor={tooltipTextColor}
				showArrow={false}
				content={<FormMultiSelectContent {...props} tooltipTextColor={tooltipTextColor} />}
				disableRefWrapper
			>
				<FormMultiSelectButton {...props} />
			</Tooltip>
		</div>
	);
};

export default FormMultiSelect;
